import { useEffect, useRef, useState } from 'react'
import { useT } from '../i18n'
import { copyText, requestShortLink, shortLinkUrl, viewAddress } from './shareLink'

/** Сколько держать итог («скопировано», ошибку), прежде чем вернуть кнопку. */
const RESULT_MS = 6000

/**
 * «Поделиться» — короткая ссылка на текущий вид карты, сразу в буфере обмена.
 *
 * Одно нажатие вместо трёх действий (выделить адрес, скопировать, извиниться за
 * его длину). Ссылка живёт 6 часов — об этом сказано на самой кнопке, а не
 * мелким шрифтом: получатель откроет её завтра и увидит 404, и это не должно
 * стать для отправителя новостью.
 *
 * Если буфер обмена недоступен (планшет на мостике по http, Safari без
 * разрешения) — показываем саму ссылку выделенной. Врать «скопировано» при
 * пустом буфере нельзя: человек вставит в чат то, что там лежало до этого.
 */
export function ShareLinkButton() {
  const t = useT()
  const [state, setState] = useState<'idle' | 'busy' | 'copied' | 'manual' | 'failed'>('idle')
  const [url, setUrl] = useState('')
  const field = useRef<HTMLInputElement>(null)
  const timer = useRef<number | null>(null)

  useEffect(() => () => {
    if (timer.current) window.clearTimeout(timer.current)
  }, [])

  const resetLater = () => {
    if (timer.current) window.clearTimeout(timer.current)
    timer.current = window.setTimeout(() => setState('idle'), RESULT_MS)
  }

  const share = async () => {
    if (state === 'busy') return
    if (timer.current) window.clearTimeout(timer.current)
    setState('busy')
    try {
      const link = await requestShortLink(viewAddress(window.location))
      const full = shortLinkUrl(window.location.origin, link.path)
      setUrl(full)
      if (await copyText(full)) {
        setState('copied')
        resetLater()
      } else {
        // Ручной путь сам не закрывается: ссылку ещё надо успеть скопировать.
        setState('manual')
      }
    } catch {
      setState('failed')
      resetLater()
    }
  }

  // Поле с ссылкой появилось — выделяем текст: остаётся одно движение, Ctrl+C.
  useEffect(() => {
    if (state === 'manual') field.current?.select()
  }, [state])

  const label =
    state === 'busy'
      ? t('map.share.creating')
      : state === 'copied'
        ? t('map.share.copied')
        : state === 'failed'
          ? t('map.share.failed')
          : t('map.share.button')

  return (
    <div className="pointer-events-auto flex flex-col items-start gap-1">
      <button
        type="button"
        onClick={share}
        disabled={state === 'busy'}
        title={t('map.share.lifetime')}
        className="flex items-center gap-1.5 rounded-lg border border-hull bg-abyss/90 px-3 py-1.5 text-xs text-foam shadow-console backdrop-blur transition-colors hover:border-sonar hover:bg-haze disabled:opacity-60"
      >
        <svg viewBox="0 0 16 16" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="1.4">
          <path d="M6.5 9.5a2.6 2.6 0 0 0 3.9.3l2-2a2.6 2.6 0 0 0-3.7-3.7l-1 1" strokeLinecap="round" />
          <path d="M9.5 6.5a2.6 2.6 0 0 0-3.9-.3l-2 2a2.6 2.6 0 0 0 3.7 3.7l1-1" strokeLinecap="round" />
        </svg>
        {label}
      </button>
      {state === 'manual' && (
        <div className="flex w-64 flex-col gap-1 rounded-lg border border-hull bg-abyss/90 p-2 shadow-console backdrop-blur">
          <div className="text-[10px] text-faint">{t('map.share.copyManually')}</div>
          <input
            ref={field}
            readOnly
            value={url}
            onFocus={event => event.currentTarget.select()}
            className="w-full rounded border border-hull bg-deep px-2 py-1 text-xs text-foam"
          />
        </div>
      )}
      {state === 'copied' && (
        <div className="rounded-lg border border-hull bg-abyss/90 px-2 py-1 text-[10px] text-faint shadow-console backdrop-blur">
          {t('map.share.lifetime')}
        </div>
      )}
    </div>
  )
}
